import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, RefreshCw, Grid, Table, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGlobalMetrics } from '@/hooks/use-global-metrics';
import { useOrganizationPipeline } from '@/hooks/use-organization-pipeline';
import { useMainAppArchivedOrgs } from '@/hooks/use-main-app-archived-orgs';
import { GlobalMetricsCards } from './global-metrics-cards';
import { OrganizationPipelineCard } from './organization-pipeline-card';
import { OrganizationPipelineTable } from './organization-pipeline-table';
import { ArchivedOrganizationsSection } from '@/components/archived-organizations-section';

const ITEMS_PER_PAGE = 15;

export function SystemAdminDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'organizations'>('overview');
  const [organizationView, setOrganizationView] = useState<'table' | 'card'>('table');
  const [cardPage, setCardPage] = useState(1);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const handleNavigateToOrg = (orgId: string) => {
    setSelectedOrgId(orgId);
    setActiveTab('organizations');
  };

  // Fetch metrics
  const { metrics, loading: metricsLoading, error: metricsError, refetch: refetchMetrics } = useGlobalMetrics();

  // Fetch organizations from the ops portal pipeline
  const { organizations: allOrganizations, loading: orgsLoading, error: orgsError, refetch: refetchOrgs } = useOrganizationPipeline();

  // Fetch archived org IDs from the main app so we can exclude them from the pipeline.
  // The ops portal has its own organizations table (statuses: ACTIVATED, PENDING_REVIEW, etc.)
  // so we can't filter by status — we must cross-reference against the main app's archive list.
  const { orgs: archivedOrgs, loading: archivedLoading, error: archivedError, refetch: refetchArchivedOrgs } = useMainAppArchivedOrgs();
  const archivedOrgIds = new Set(archivedOrgs.map((o) => o.id));
  const organizations = allOrganizations.filter((org) => !archivedOrgIds.has(org.id));

  const handleRefresh = () => {
    refetchMetrics();
    refetchOrgs();
    refetchArchivedOrgs();
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100'>
            System Admin Dashboard
          </h1>
          <p className='text-gray-600 dark:text-gray-400 mt-1'>
            Global overview of onboarding progress and tasks
          </p>
        </div>

        <Button
          onClick={handleRefresh}
          disabled={metricsLoading || orgsLoading}
          className='flex items-center gap-2'
        >
          <RefreshCw className='h-4 w-4' />
          {metricsLoading || orgsLoading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Error Messages */}
      {metricsError && (
        <Card className='border-red-200 dark:border-red-800'>
          <CardContent className='pt-6 flex items-start gap-3'>
            <AlertCircle className='h-5 w-5 text-red-600 flex-shrink-0 mt-0.5' />
            <div>
              <p className='text-red-600 dark:text-red-400'>
                Error loading metrics: {metricsError.message}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {orgsError && (
        <Card className='border-red-200 dark:border-red-800'>
          <CardContent className='pt-6 flex items-start gap-3'>
            <AlertCircle className='h-5 w-5 text-red-600 flex-shrink-0 mt-0.5' />
            <div>
              <p className='text-red-600 dark:text-red-400'>
                Error loading organizations: {orgsError.message}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)} className='w-full'>
        <TabsList className='grid w-full grid-cols-2'>
          <TabsTrigger value='overview'>Overview</TabsTrigger>
          <TabsTrigger value='organizations'>Organizations</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value='overview' className='space-y-6'>
          <GlobalMetricsCards metrics={metrics} />

          {/* Recent Organizations Preview */}
          {organizations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Organizations</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                  {organizations.slice(0, 6).map((org) => (
                    <OrganizationPipelineCard 
                      key={org.id} 
                      organization={org}
                      onNavigate={handleNavigateToOrg}
                    />
                  ))}
                </div>

                {organizations.length > 6 && (
                  <div className='flex justify-center pt-4'>
                    <Button onClick={() => setActiveTab('organizations')} className='w-full md:w-auto'>
                      Show More Organizations
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value='organizations' className='space-y-4'>
          {/* View Toggle */}
          <div className='flex items-center justify-between'>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>All Organizations</h2>
            <div className='flex gap-2'>
              <Button
                onClick={() => {
                  setOrganizationView('table');
                  setCardPage(1);
                }}
                variant={organizationView === 'table' ? 'default' : 'outline'}
                size='sm'
                className='flex items-center gap-2'
              >
                <Table className='h-4 w-4' />
                Table
              </Button>
              <Button
                onClick={() => {
                  setOrganizationView('card');
                  setCardPage(1);
                }}
                variant={organizationView === 'card' ? 'default' : 'outline'}
                size='sm'
                className='flex items-center gap-2'
              >
                <Grid className='h-4 w-4' />
                Cards
              </Button>
            </div>
          </div>

          {/* Table View */}
          {organizationView === 'table' && (
            <OrganizationPipelineTable
              organizations={organizations}
              loading={orgsLoading}
              initialExpandedOrgId={selectedOrgId}
              onOrgArchived={() => { refetchArchivedOrgs(); }}
              onLocationChanged={() => { refetchArchivedOrgs(); }}
            />
          )}

          {/* Card View */}
          {organizationView === 'card' && !orgsLoading && organizations.length > 0 && (
            <div className='space-y-4'>
              {/* Cards Grid */}
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                {organizations
                  .slice((cardPage - 1) * ITEMS_PER_PAGE, cardPage * ITEMS_PER_PAGE)
                  .map((org) => (
                    <OrganizationPipelineCard 
                      key={org.id} 
                      organization={org}
                      onNavigate={handleNavigateToOrg}
                    />
                  ))}
              </div>

              {/* Pagination */}
              {organizations.length > ITEMS_PER_PAGE && (
                <div className='flex items-center justify-between pt-4'>
                  <div className='text-sm text-gray-600 dark:text-gray-400'>
                    Showing {(cardPage - 1) * ITEMS_PER_PAGE + 1}-
                    {Math.min(cardPage * ITEMS_PER_PAGE, organizations.length)} of {organizations.length}{' '}
                    organizations
                  </div>

                  <div className='flex items-center gap-2'>
                    <Button
                      onClick={() => setCardPage(Math.max(1, cardPage - 1))}
                      disabled={cardPage === 1}
                      variant='outline'
                      size='sm'
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </Button>

                    <div className='flex items-center gap-1'>
                      {Array.from({ length: Math.ceil(organizations.length / ITEMS_PER_PAGE) }).map((_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            onClick={() => setCardPage(page)}
                            variant={cardPage === page ? 'default' : 'outline'}
                            size='sm'
                            className='min-w-10'
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      onClick={() =>
                        setCardPage(
                          Math.min(Math.ceil(organizations.length / ITEMS_PER_PAGE), cardPage + 1)
                        )
                      }
                      disabled={cardPage === Math.ceil(organizations.length / ITEMS_PER_PAGE)}
                      variant='outline'
                      size='sm'
                    >
                      <ChevronRight className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Loading State for Card View */}
          {organizationView === 'card' && orgsLoading && (
            <div className='flex items-center justify-center py-12'>
              <p className='text-gray-600 dark:text-gray-400'>Loading organizations...</p>
            </div>
          )}

          {/* Archived Organizations Section */}
          <ArchivedOrganizationsSection
            orgs={archivedOrgs}
            loading={archivedLoading}
            error={archivedError}
            refetch={refetchArchivedOrgs}
          />

          {/* Empty State */}
          {!orgsLoading && organizations.length === 0 && (
            <Card>
              <CardContent className='pt-6'>
                <div className='text-center py-8'>
                  <p className='text-gray-600 dark:text-gray-400'>No organizations found</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
